/**
 * Base entity model. All Apollon models should inherit this class.
 *
 * This file is part of Zeus Apollon
 *
 * (c) Kaden und Partner AG
 */
import { BaseModel } from '@/models/BaseModel';
import { BaseCollection } from '@/models/BaseCollection';
import { instance as getApi } from '@/helpers/api.js';
import dayjs, { Dayjs } from 'dayjs';
import type { MutationsObject } from '@kp/js-model';

export type Building = {
    egid: string,
    assekuranz_nummer: string,
    parzelleNr: string,
    strasse: string,
    strassen_nummer: string,
    ort: string,
    plz: string,
    plz_zusatz: string,
    x: number,
    y: number,
    baujahr: string,
    gemeinde: string,
    renovationsjahr: string,
    gebaeudename: string,
    gebaeude_status: string,
    gebaeude_kategorie: string,
    gebaeude_klasse: string,
    gebaeude_flaeche: string,
    gebaeude_volumen: string,
    anzahl_geschosse: string,
    anzahl_wohnungen: string,
    heizung_versorgung: string,
    energietraeger_heizung: string,
    warmwasser_versorgung: string,
    energietraeger_warmwasser: string,
    heizung_versorgung_2: string,
    energietraeger_heizung_2: string,
    warmwasser_versorgung_2: string,
    energietraeger_warmwasser_2: string,
    energiebezugsflaeche: string,
}

export class Liegenschaft extends BaseModel {
    id: number | null = null;
    strasse: string | null = null;
    strassen_nummer: string | null = null;
    ort_id: number | null = null;
    gemeinde_id: number | null = null;
    ist_neubau: boolean = false;
    egid: number | null = null;
    baujahr: number | null = null;
    baujahr_ist_geschaetzt: boolean = false;
    parzellen_nummer: string | null = null;
    assekuranz_nummer: string | null = null;
    coord_x: number | null = null;
    coord_y: number | null = null;
    created: Dayjs | null = null;
    modified: Dayjs | null = null;
    ersteller_id: number | null = null;

    // View fields
    adresse: string | null = null;
    gemeinde_bezeichnung: string | null = null;
    kanton_kuerzel: string | null = null;
    kanton_name: string | null = null;
    ort_plz: string | null = null;
    ort: string | null = null;
    ort_kurz: string | null = null;

    public mutations(): MutationsObject {
        return {
            id: (id: number) => (Number(id) > 0 ? Number(id) : null),
            ist_neubau: Boolean,
            baujahr_ist_geschaetzt: Boolean,
            created: dayjs,
            modified: dayjs
        };
    }

    getClassName(): string {
        return 'liegenschaft';
    }

    /**
     * Fetches and returns the GWR information from the API.
     * Uses Ort-ID, Strasse, Nr.
     *
     * Returns a promise to a object with the GWR data if matched, else null.
     */
    async egidQuery() {
        const res = await getApi().get('/lookup/egidQuery', {
            params: {
                ort_id: this.ort_id,
                gemeinde_id: this.gemeinde_id,
                strasse: this.strasse,
                strassen_nummer: this.strassen_nummer
            }
        });
        if (res?.data?.data) {
            return res.data.data as Building;
        }
    }
}

export class LiegenschaftCollection extends BaseCollection<Liegenschaft> {
    protected modelCls = Liegenschaft;

    public getModelClassName(): string {
        return 'liegenschaft';
    }
}
